import { useEffect, useState } from 'react';

/**
 * SpeakingGlow — admin-console palette
 *
 * 暖色調（accent / amber-tan + cream），pulse 速度依 isTalking 切換。
 * 純 CSS animation，無外部依賴，效能優先。
 */
export default function SpeakingGlow({ isTalking = false, children }) {
    // accent-700 amber tan + cream tones（取代 qubbyai 的藍紫）
    const colors = [
        '#a89060', // accent-700
        '#c8a578', // accent-500-ish
        '#e4cca5', // accent-200
        '#d9b685', // accent-400
        '#b89970', // accent-600
        '#efe0c5', // accent-100
    ];

    const [isFocused, setIsFocused] = useState(true);

    useEffect(() => {
        const onVis = () => setIsFocused(!document.hidden);
        const onFocus = () => setIsFocused(true);
        const onBlur = () => setIsFocused(false);
        document.addEventListener('visibilitychange', onVis);
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    return (
        <div className="speaking-glow-root">
            <div
                className={`glow ${isTalking ? 'talking' : ''} ${!isFocused ? 'paused' : ''}`}
                style={{
                    '--c1': colors[0],
                    '--c2': colors[1],
                    '--c3': colors[2],
                    '--c4': colors[3],
                    '--c5': colors[4],
                    '--c6': colors[5],
                }}
            >
                <div className="glow-shadow" aria-hidden />
            </div>
            {children}

            <style>{`
                .speaking-glow-root {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: visible;
                }
                .glow {
                    --size: 240px;
                    --inner-min: 90px;
                    --inner-max: 120px;
                    --pulse-speed: 1.4s;
                    --rotate-speed: 4s;
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    transition: opacity 0.5s ease;
                    pointer-events: none;
                    opacity: 0;
                }
                .glow.talking {
                    --inner-min: 80px;
                    --inner-max: 140px;
                    --pulse-speed: 0.6s;
                    --rotate-speed: 1.6s;
                    opacity: 1;
                }
                .glow::before {
                    content: "";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-radius: 50%;
                    z-index: -1;
                    width: var(--size);
                    height: var(--size);
                    background: rgba(168, 144, 96, 0.06);
                    opacity: 0.8;
                }
                .glow::after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-radius: 50%;
                    z-index: -1;
                    width: var(--inner-min);
                    height: var(--inner-min);
                    background: rgba(200, 165, 120, 0.15);
                    animation: pulse var(--pulse-speed) ease-in-out infinite alternate;
                }
                .glow-shadow {
                    width: var(--size);
                    height: var(--size);
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    border-radius: 50%;
                    z-index: -1;
                    box-shadow:
                        0 0 25px 8px var(--c1),
                        0 0 50px 12px var(--c2),
                        0 0 80px 20px var(--c3),
                        4px -8px 30px 8px var(--c4),
                        -6px 6px 35px 10px var(--c5);
                    animation: rotate var(--rotate-speed) linear infinite;
                    transform-origin: center;
                    transform: translate(-50%, -50%) rotate(0deg);
                    filter: blur(20px);
                    opacity: 0.55;
                    will-change: transform;
                }
                .glow.talking .glow-shadow {
                    box-shadow:
                        0 0 35px 12px var(--c1),
                        0 0 65px 18px var(--c2),
                        0 0 100px 28px var(--c3),
                        6px -10px 40px 12px var(--c4),
                        -8px 8px 45px 14px var(--c5);
                    filter: blur(24px);
                    opacity: 0.75;
                }
                .glow.paused .glow-shadow,
                .glow.paused::after {
                    animation-play-state: paused !important;
                }
                @keyframes pulse {
                    from { width: var(--inner-min); height: var(--inner-min); }
                    to   { width: var(--inner-max); height: var(--inner-max); }
                }
                @keyframes rotate {
                    from { transform: translate(-50%, -50%) rotate(0deg); }
                    to   { transform: translate(-50%, -50%) rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
